import React, { useEffect, useMemo, useState } from "react";
import { Modal, message } from "antd";
import {
  ArrowRight,
  Barcode,
  Boxes,
  Check,
  Clock,
  Copy,
  FileSpreadsheet,
  GitCompare,
  History,
  Inbox,
  Layers,
  Link2,
  MapPin,
  Nfc,
  Package,
  Plus,
  QrCode,
  Radio,
  RefreshCw,
  Smartphone,
  Trash2,
  User,
  Workflow,
} from "lucide-react";
import moment from "moment";
import * as XLSX from "xlsx";
import UITooltip from "../../../common/tooltip";
import "./style.css";

interface EpcisHistoryModalProps {
  visible: boolean;
  onClose: () => void;
  diId: string | null;
  loading: boolean;
  data: any;
  /** The list row the history was opened from — supplies the tag's product /
   * location context, which the event payloads alone don't establish. */
  record?: any;
}

const EM_DASH = "—";

const Tip = ({ title, children }: { title?: any; children: React.ReactElement }) => (
  <UITooltip title={title} placement="top">
    {children}
  </UITooltip>
);

// ── Action vocabulary ──────────────────────────────────────────────────────────
// Green for a create, orange for a change, red for a removal, blue for a
// passive observation.
const ACTION_META: Record<string, { label: string; tone: string; icon: any }> = {
  add: { label: "Add", tone: "add", icon: Plus },
  update: { label: "Update", tone: "update", icon: RefreshCw },
  delete: { label: "Delete", tone: "delete", icon: Trash2 },
  observe: { label: "Observe", tone: "observe", icon: Boxes },
};

const getActionMeta = (action: any) =>
  ACTION_META[String(action ?? "").toLowerCase()] ?? {
    label: String(action ?? "Event"),
    tone: "observe",
    icon: Boxes,
  };

// Fields compared against the previous event to build the "what changed" bar.
const DIFF_FIELDS: { key: string; label: string }[] = [
  { key: "status", label: "Status" },
  { key: "siteName", label: "Site" },
  { key: "zoneName", label: "Zone" },
  { key: "bizStep", label: "Biz step" },
  { key: "disposition", label: "Disposition" },
  { key: "productUPC", label: "Product UPC" },
  { key: "userName", label: "User" },
  { key: "deviceName", label: "Device" },
  { key: "processName", label: "Process" },
];

const truncate = (value: any, max = 18): string => {
  const text = String(value ?? EM_DASH);
  return text.length > max ? `${text.slice(0, max)}…` : text;
};

const getEventTime = (record: any) =>
  record?.operationTime || record?.eventTime || record?.createdAt;

const formatEventTime = (record: any): string => {
  const time = getEventTime(record);
  return time ? moment(time).format("DD MMM YYYY, hh:mm A") : EM_DASH;
};

// ── Markers + status ───────────────────────────────────────────────────────────
type MarkerType = "UHF" | "NFC" | "BARCODE" | "QR";

const MARKER_META: Record<MarkerType, { label: string; icon: any }> = {
  UHF: { label: "UHF RFID", icon: Radio },
  NFC: { label: "NFC", icon: Nfc },
  BARCODE: { label: "Barcode", icon: Barcode },
  QR: { label: "QR Code", icon: QrCode },
};

// Onto the four marker slots from the many spellings the payload uses.
const normalizeMarkerType = (raw: any): MarkerType | undefined => {
  const value = String(raw ?? "").toUpperCase();
  if (!value) return undefined;
  if (value.includes("UHF") || value.includes("RFID")) return "UHF";
  if (value.includes("NFC")) return "NFC";
  if (value.includes("QR")) return "QR";
  if (value.startsWith("BAR")) return "BARCODE";
  return undefined;
};

const MarkerTag = ({ type }: { type: any }) => {
  const normalized = normalizeMarkerType(type);
  const Icon = normalized ? MARKER_META[normalized].icon : Barcode;
  return (
    <span className={normalized ? `epcis-marker epcis-marker-${normalized}` : "epcis-marker"}>
      <Icon strokeWidth={2} />
      {normalized ? MARKER_META[normalized].label : String(type ?? EM_DASH)}
    </span>
  );
};

const getStatusTone = (status: any): "enabled" | "deenabled" | "duplicate" => {
  const value = String(status ?? "").toLowerCase();
  if (value.includes("duplicate")) return "duplicate";
  if (value.includes("de-enable") || value.includes("deenable") || value.includes("disable"))
    return "deenabled";
  return "enabled";
};

const STATUS_LABEL = {
  enabled: "ENABLED",
  deenabled: "DE-ENABLED",
  duplicate: "DUPLICATE",
};

const StatusBadge = ({ status }: { status: any }) => {
  const tone = getStatusTone(status);
  return <span className={`epcis-status ${tone}`}>{STATUS_LABEL[tone]}</span>;
};

// ── Cells ──────────────────────────────────────────────────────────────────────
const InfoCell = ({
  label,
  value,
  changed,
  mono,
  children,
}: {
  label: string;
  value?: any;
  changed?: boolean;
  mono?: boolean;
  children?: React.ReactNode;
}) => (
  <div className={changed ? "epcis-cell epcis-cell--changed" : "epcis-cell"}>
    <span className="epcis-cell-label">
      {label}
      {changed && <span className="epcis-changed-dot" />}
    </span>
    {children ?? (
      <Tip title={value ? String(value) : undefined}>
        <span className={mono ? "epcis-cell-value epcis-mono" : "epcis-cell-value"}>
          {value === null || value === undefined || value === "" ? EM_DASH : String(value)}
        </span>
      </Tip>
    )}
  </div>
);

const ContextItem = ({
  icon,
  value,
  changed,
}: {
  icon: React.ReactNode;
  value: any;
  changed?: boolean;
}) => (
  <span className={changed ? "epcis-ctx-item changed" : "epcis-ctx-item"}>
    {icon}
    <span className="epcis-ctx-value">{value || EM_DASH}</span>
    {changed && <span className="epcis-changed-dot" />}
  </span>
);

// ── EventCard ──────────────────────────────────────────────────────────────────
const EventCard = ({
  record,
  seqNum,
  prevRecord,
}: {
  record: any;
  seqNum: number;
  prevRecord?: any;
}) => {
  const action = getActionMeta(record?.action);
  const ActionIcon = action.icon;

  const additionalData = record?.additionalData ?? {};
  const associations: any[] = Array.isArray(additionalData.association)
    ? additionalData.association
    : [];
  const encodes: any[] = Array.isArray(additionalData.encode) ? additionalData.encode : [];
  const addInputs: any[] = Array.isArray(additionalData.addInput)
    ? additionalData.addInput.filter((item: any) => item?.key || item?.value)
    : [];

  const isChanged = (key: string) =>
    !!prevRecord && String(record?.[key] ?? "") !== String(prevRecord?.[key] ?? "");

  const changes = prevRecord ? DIFF_FIELDS.filter(({ key }) => isChanged(key)) : [];

  const prevData = prevRecord?.additionalData ?? {};
  const prevAssociations: any[] = Array.isArray(prevData.association) ? prevData.association : [];
  const prevEncodes: any[] = Array.isArray(prevData.encode) ? prevData.encode : [];

  const assocChanged =
    !!prevRecord &&
    JSON.stringify(associations.map((a: any) => a?.code).sort()) !==
      JSON.stringify(prevAssociations.map((a: any) => a?.code).sort());
  const encodeChanged =
    !!prevRecord &&
    JSON.stringify(encodes.map((e: any) => e?.id).sort()) !==
      JSON.stringify(prevEncodes.map((e: any) => e?.id).sort());

  const CountChange = ({ label, from, to }: { label: string; from: number; to: number }) => (
    <span className="epcis-change">
      <span className="epcis-change-field">{label}</span>
      <span className="epcis-change-old">{from}</span>
      <ArrowRight size={11} strokeWidth={2.5} className="epcis-change-arrow" />
      <span className="epcis-change-new">{to}</span>
    </span>
  );

  return (
    <div className="epcis-event">
      {/* The timeline node carries the sequence number itself. */}
      <div className={`epcis-node epcis-tone-${action.tone}`}>{seqNum}</div>

      <div className="epcis-card">
        <div className={`epcis-card-head epcis-tone-${action.tone}`}>
          <span className={`epcis-action epcis-tone-${action.tone}`}>
            <ActionIcon size={12} strokeWidth={2.5} />
            {action.label}
          </span>
          <span className="epcis-time">
            <Clock size={12} strokeWidth={2} />
            {formatEventTime(record)}
          </span>
        </div>

        {(changes.length > 0 || assocChanged || encodeChanged) && (
          <div className="epcis-changes">
            <span className="epcis-changes-head">
              <GitCompare size={13} strokeWidth={2} />
              What changed
            </span>
            <div className="epcis-changes-list">
              {changes.map(({ key, label }) => (
                <span key={key} className="epcis-change">
                  <span className="epcis-change-field">{label}</span>
                  <Tip title={String(prevRecord?.[key] ?? EM_DASH)}>
                    <span className="epcis-change-old">{truncate(prevRecord?.[key])}</span>
                  </Tip>
                  <ArrowRight size={11} strokeWidth={2.5} className="epcis-change-arrow" />
                  <Tip title={String(record?.[key] ?? EM_DASH)}>
                    <span className="epcis-change-new">{truncate(record?.[key])}</span>
                  </Tip>
                </span>
              ))}
              {assocChanged && (
                <CountChange label="Markers" from={prevAssociations.length} to={associations.length} />
              )}
              {encodeChanged && (
                <CountChange label="Encodes" from={prevEncodes.length} to={encodes.length} />
              )}
            </div>
          </div>
        )}

        <div className="epcis-grid">
          <InfoCell label="Status" changed={isChanged("status")}>
            <StatusBadge status={record?.status} />
          </InfoCell>
          <InfoCell label="Site" value={record?.siteName} changed={isChanged("siteName")} />
          <InfoCell label="Zone" value={record?.zoneName} changed={isChanged("zoneName")} />
          <InfoCell
            label="Product UPC"
            value={record?.productUPC}
            changed={isChanged("productUPC")}
            mono
          />
          <InfoCell label="Biz step" value={record?.bizStep} changed={isChanged("bizStep")} />
          <InfoCell
            label="Disposition"
            value={record?.disposition}
            changed={isChanged("disposition")}
          />
        </div>

        <div className="epcis-ctx">
          <ContextItem
            icon={<User size={12} strokeWidth={2} />}
            value={record?.userName}
            changed={isChanged("userName")}
          />
          <span className="epcis-ctx-sep" />
          <ContextItem
            icon={<Smartphone size={12} strokeWidth={2} />}
            value={record?.deviceName}
            changed={isChanged("deviceName")}
          />
          <span className="epcis-ctx-sep" />
          <ContextItem
            icon={<Workflow size={12} strokeWidth={2} />}
            value={record?.processName}
            changed={isChanged("processName")}
          />
        </div>

        {associations.length > 0 && (
          <div className="epcis-section">
            <div className="epcis-section-head">
              <span className="epcis-section-title">Marker associations</span>
              {assocChanged && <span className="epcis-changed-badge">Changed</span>}
            </div>
            <div className="epcis-assoc-list">
              {associations.map((assoc: any, index: number) => {
                const meta = Object.entries(assoc?.metaInfo ?? {}).filter(
                  ([, value]) => value !== null && value !== undefined && value !== "",
                );
                return (
                  <div className="epcis-assoc" key={`${assoc?.code}-${index}`}>
                    <div className="epcis-assoc-head">
                      <MarkerTag type={assoc?.type} />
                      <Tip title={assoc?.code}>
                        <span className="epcis-assoc-code">{assoc?.code || EM_DASH}</span>
                      </Tip>
                    </div>
                    {meta.length > 0 && (
                      <div className="epcis-meta-list">
                        {meta.map(([key, value]) => (
                          <span className="epcis-meta" key={key}>
                            <span className="epcis-meta-key">{key}</span>
                            <Tip title={String(value)}>
                              <span className="epcis-meta-value">{String(value)}</span>
                            </Tip>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {encodes.length > 0 && (
          <div className="epcis-section">
            <div className="epcis-section-head">
              <span className="epcis-section-title">Encode data</span>
              {encodeChanged && <span className="epcis-changed-badge">Changed</span>}
            </div>
            <div className="epcis-assoc-list">
              {encodes.map((encode: any, index: number) => (
                <div className="epcis-assoc epcis-assoc--full" key={`encode-${index}`}>
                  <div className="epcis-assoc-head">
                    <MarkerTag type={encode?.type ?? "NFC"} />
                    <Tip title={encode?.id}>
                      <span className="epcis-assoc-code">{encode?.id || EM_DASH}</span>
                    </Tip>
                  </div>
                  {encode?.url && (
                    <div className="epcis-encode-url">
                      <Link2 size={12} strokeWidth={2} />
                      <Tip title={encode.url}>
                        <a
                          href={encode.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="epcis-link"
                        >
                          {encode.url}
                        </a>
                      </Tip>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {addInputs.length > 0 && (
          <div className="epcis-section">
            <div className="epcis-section-head">
              <span className="epcis-section-title">Additional inputs</span>
            </div>
            <div className="epcis-meta-list">
              {addInputs.map((input: any, index: number) => (
                <span className="epcis-meta" key={`${input?.key}-${index}`}>
                  <span className="epcis-meta-key">{input?.key}</span>
                  <span className="epcis-meta-value">{String(input?.value ?? EM_DASH)}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const TimelineSkeleton = () => (
  <div className="epcis-timeline">
    {[0, 1, 2].map((row) => (
      <div className="epcis-event" key={row}>
        <div className="epcis-node epcis-node--skeleton" />
        <div className="epcis-card epcis-card--skeleton">
          <div className="epcis-card-head">
            <span className="epcis-skel" style={{ width: 86, height: 20, borderRadius: 999 }} />
            <span className="epcis-skel" style={{ width: 150, height: 14 }} />
          </div>
          <div className="epcis-grid">
            {[0, 1, 2, 3, 4, 5].map((cell) => (
              <div className="epcis-cell" key={cell}>
                <span className="epcis-skel" style={{ width: "46%", height: 9 }} />
                <span className="epcis-skel" style={{ width: "76%", height: 14 }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    ))}
  </div>
);

// ── Main Modal ─────────────────────────────────────────────────────────────────
const EpcisHistoryModal: React.FC<EpcisHistoryModalProps> = ({
  visible,
  onClose,
  diId,
  loading,
  data,
  record,
}) => {
  const [copied, setCopied] = useState(false);

  const records: any[] = useMemo(() => (Array.isArray(data?.data) ? data.data : []), [data]);
  const totalCount: number = data?.pagination?.total ?? records.length;

  // The list row is optional; fall back to the newest event for the context
  // strip so callers that don't pass it still get product / location.
  const context = record ?? records[records.length - 1];

  useEffect(() => {
    if (!visible) setCopied(false);
  }, [visible]);

  const copyTagId = async () => {
    if (!diId) return;
    try {
      await navigator.clipboard.writeText(diId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard unavailable (insecure origin) — nothing worth failing over */
    }
  };

  // Excel History Export Routine
  const handleExportHistoryExcel = () => {
    if (!records || records.length === 0) {
      message.warning("No tracking history data available to export.");
      return;
    }

    try {
      // Unpack nested associations, encodes, and inputs into flat rows
      const exportRows = records.map((row, index) => {
        const assocData = row.additionalData?.association || [];
        const encodeData = row.additionalData?.encode || [];
        const addInputs = row.additionalData?.addInput || [];

        return {
          Sequence: index + 1,
          Action: row.action || "—",
          Timestamp: formatEventTime(row),
          "Tag ID": row.diId || "—",
          Status: row.status || "—",
          Site: row.siteName || "—",
          Zone: row.zoneName || "—",
          "Product UPC": row.productUPC || "—",
          Disposition: row.disposition || "—",
          "Biz Step": row.bizStep || "—",
          User: row.userName || "—",
          Device: row.deviceName || "—",
          Process: row.processName || "—",
          "Marker Associations":
            assocData.map((a: any) => `[${a.type}]: ${a.code}`).join("; ") || "None",
          "Encode Identifiers":
            encodeData.map((e: any) => `[${e.type || "NFC"}]: ${e.id}`).join("; ") || "None",
          "Additional Inputs":
            addInputs.map((i: any) => `${i.key}: ${i.value}`).join("; ") || "None",
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "EPCIS History Log");

      worksheet["!cols"] = [
        { wch: 10 }, // Sequence
        { wch: 12 }, // Action
        { wch: 22 }, // Timestamp
        { wch: 24 }, // Tag ID
        { wch: 14 }, // Status
        { wch: 20 }, // Site
        { wch: 16 }, // Zone
        { wch: 18 }, // Product UPC
        { wch: 22 }, // Disposition
        { wch: 20 }, // Biz Step
        { wch: 16 }, // User
        { wch: 16 }, // Device
        { wch: 18 }, // Process
        { wch: 35 }, // Marker Associations
        { wch: 35 }, // Encode Identifiers
        { wch: 30 }, // Additional Inputs
      ];

      XLSX.writeFile(
        workbook,
        `EPCIS_History_${diId || "Export"}_${moment().format("YYYY-MM-DD")}.xlsx`,
      );
      message.success("Event history log exported successfully!");
    } catch (error) {
      console.error("History file generation crash encountered", error);
      message.error("Failed to compile timeline spreadsheet export.");
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      width={900}
      destroyOnClose
      closable
      footer={null}
      wrapClassName="epcisHistoryModal"
      title={
        <div className="epcis-modal-header">
          <span className="epcis-modal-header-icon">
            <History size={18} strokeWidth={2} />
          </span>
          <div>
            <div className="epcis-modal-header-title">EPCIS Event History</div>
            <div className="epcis-modal-header-subtitle">
              Every recorded event for this digital identity, with what changed
              since the event before it highlighted.
            </div>
          </div>
        </div>
      }
    >
      {/* Context strip: which tag, which product, where. */}
      <div className="epcis-summary">
        <div className="epcis-summary-main">
          <span className="epcis-summary-label">Tag id</span>
          <div className="epcis-summary-tag">
            <span className="epcis-mono epcis-summary-tagValue">{diId || EM_DASH}</span>
            {diId && (
              <button
                type="button"
                className="epcis-copy"
                onClick={copyTagId}
                aria-label="Copy tag id"
              >
                {copied ? <Check size={13} strokeWidth={2.5} /> : <Copy size={13} strokeWidth={2} />}
              </button>
            )}
          </div>
        </div>

        <div className="epcis-summary-facts">
          <div className="epcis-fact">
            <Package size={13} strokeWidth={2} />
            <div>
              <span className="epcis-fact-label">Product</span>
              <span className="epcis-fact-value">
                {context?.productDescription || context?.productName || context?.productUPC || EM_DASH}
              </span>
            </div>
          </div>
          <div className="epcis-fact">
            <MapPin size={13} strokeWidth={2} />
            <div>
              <span className="epcis-fact-label">Site / zone</span>
              <span className="epcis-fact-value">
                {[context?.siteName, context?.zoneName].filter(Boolean).join(" · ") || EM_DASH}
              </span>
            </div>
          </div>
          <div className="epcis-fact">
            <QrCode size={13} strokeWidth={2} />
            <div>
              <span className="epcis-fact-label">Events</span>
              <span className="epcis-fact-value">{loading ? EM_DASH : totalCount}</span>
            </div>
          </div>
          {records.length > 0 && (
            <button type="button" className="epcis-export-btn" onClick={handleExportHistoryExcel}>
              <FileSpreadsheet size={14} strokeWidth={2} />
              Export
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <TimelineSkeleton />
      ) : records.length === 0 ? (
        <div className="epcis-empty">
          <span className="epcis-empty-icon">
            <Inbox size={26} strokeWidth={2} />
          </span>
          <div className="epcis-empty-title">No EPCIS events yet</div>
          <div className="epcis-empty-desc">
            Nothing has been recorded against this digital identity. Events appear here as the tag
            moves through your processes.
          </div>
        </div>
      ) : (
        <div className="epcis-timeline">
          {records.map((row: any, index: number) => (
            <EventCard
              key={row?._id ?? index}
              record={row}
              prevRecord={index > 0 ? records[index - 1] : undefined}
              seqNum={index + 1}
            />
          ))}

          {totalCount > records.length && (
            <div className="epcis-truncated">
              <Layers size={13} strokeWidth={2} />
              Showing {records.length} of {totalCount} events for this tag.
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default EpcisHistoryModal;
